import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchChildrenSymptoms,
  fetchCropDetails,
  fetchNextSymptomsOrDisease,
  fetchSymptomsByLevel,
} from '../api/cropService';
import { useSymptomFlow } from '../hooks/useSymptomFlow';

const SymptomsPage = () => {
  const { t } = useTranslation();
  const { cid } = useParams();
  const cropId = parseInt(cid, 10);
  const {
    symptoms,
    setSymptoms,
    currentLevel,
    setCurrentLevel,
    history,
    setHistory,
    loading,
    setLoading,
    handleBack
  } = useSymptomFlow(cropId);
  
  const [popupSymptom, setPopupSymptom] = useState(null);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [cropName, setCropName] = useState("Unknown Crop");
  const [showCustomSymptomDialog, setShowCustomSymptomDialog] = useState(false);
  const navigate = useNavigate();

  // Track selected symptoms across levels - this will store all selected symptoms
  const [allSelectedSymptoms, setAllSelectedSymptoms] = useState([]);
  // Track if we're returning from advice page
  const [returningFromAdvice, setReturningFromAdvice] = useState(false);

  // Check if a symptom is selected - updated logic
  const isSymptomSelected = (sym) => {
    // Check if this symptom is in the current history
    const inHistory = history.some(s => s.sid === sym.sid);
    
    // Check if this symptom is in all selected symptoms (for returning from advice)
    const inAllSelected = allSelectedSymptoms.some(s => s.sid === sym.sid);
    
    // Check if this is the current popup symptom
    const isPopupSymptom = popupSymptom && popupSymptom.sid === sym.sid;
    
    return inHistory || inAllSelected || isPopupSymptom;
  };

  // Update handleBack to preserve selected symptoms
  const handleBackWithHistory = () => {
    handleBack();
  };

  // Load initial symptoms and crop name
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Check if we're returning from AdvicePage with saved state
        const savedFlowState = sessionStorage.getItem('symptomFlowState');
        
        if (savedFlowState) {
          const flowState = JSON.parse(savedFlowState);
          
          if (flowState.returnFromAdvice) {
            // We're returning from advice page
            setReturningFromAdvice(true);
            
            console.log("Restoring flow state:", flowState);
            
            // Restore the symptom flow state
            setHistory(flowState.history || []);
            setCurrentLevel(flowState.currentLevel || 1);
            
            // Store all selected symptoms for highlighting (including the one we just removed)
            setAllSelectedSymptoms(flowState.allSelectedSymptoms || []);
            
            // Fetch the appropriate symptoms for the current state
            if (flowState.history && flowState.history.length > 0) {
              const selectedSymptomIds = flowState.history.map(s => s.sid);
              const result = await fetchNextSymptomsOrDisease(cropId, selectedSymptomIds);
              
              if (result.symptoms && result.symptoms.length > 0) {
                setSymptoms(result.symptoms);
              } else {
                // If no more symptoms, load level 1 symptoms
                const symptomsData = await fetchSymptomsByLevel(cropId, 1);
                setSymptoms(symptomsData);
              }
            } else {
              // No history, load level 1 symptoms
              const symptomsData = await fetchSymptomsByLevel(cropId, 1);
              setSymptoms(symptomsData);
            }
            
            // Clear the saved state
            sessionStorage.removeItem('symptomFlowState');
          } else {
            // Normal initial load
            const symptomsData = await fetchSymptomsByLevel(cropId, 1);
            setSymptoms(symptomsData);
            setCurrentLevel(1);
            setAllSelectedSymptoms([]);
          }
        } else {
          // Normal initial load
          const symptomsData = await fetchSymptomsByLevel(cropId, 1);
          setSymptoms(symptomsData);
          setCurrentLevel(1);
          setAllSelectedSymptoms([]);
        }

        const cropDetails = await fetchCropDetails(cropId);
        console.log("Crop details:", cropDetails);
        setCropName(cropDetails.name || "Unknown Crop");
      } catch (err) {
        console.error("Error loading initial data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [cropId]);

  const handleYesClick = (sym) => {
    setPopupSymptom(sym);
  };

  const handleCustomSymptomClick = () => {
    setShowCustomSymptomDialog(true);
  };

  const handleCustomSymptomDialogClose = () => {
    setShowCustomSymptomDialog(false);
  };

  const handlePopupBack = () => {
    // Close the popup first
    setPopupSymptom(null);
    
    // If we returned from advice page, clear that state
    const savedFlowState = sessionStorage.getItem('symptomFlowState');
    if (savedFlowState) {
      try {
        const flowState = JSON.parse(savedFlowState);
        if (flowState.returnFromAdvice) {
          sessionStorage.removeItem('symptomFlowState');
        }
      } catch (err) {
        console.error('Error parsing flow state:', err);
      }
    }
  };

  const handlePopupConfirm = async () => {
    setLoading(true);
    try {
      const updatedHistory = [...history, popupSymptom];
      setHistory(updatedHistory);
      
      // Update all selected symptoms
      const updatedAllSelected = [...allSelectedSymptoms];
      if (!updatedAllSelected.some(s => s.sid === popupSymptom.sid)) {
        updatedAllSelected.push(popupSymptom);
      }
      setAllSelectedSymptoms(updatedAllSelected);
      
      const selectedSymptomIds = updatedHistory.map(s => s.sid);
      const result = await fetchNextSymptomsOrDisease(cropId, selectedSymptomIds);

      if (result.disease) {
        const diagnosisData = {
          disId: result.disease.disId,
          chemicalName: result.disease.chemicalName || t('advice.noAdviceData'),
          scientificName: result.disease.scientificName || t('advice.noAdviceData'),
          chemicalAdvice: result.disease.chemicalAdvice || t('advice.noAdviceData'),
          organicAdvice: result.disease.organicAdvice || t('advice.noAdviceData'),
          ipmMeasures: result.disease.ipmMeasures || t('advice.noAdviceData'),
          otherSymptoms: Array.isArray(result.otherSymptoms) ? result.otherSymptoms : [],
          confirmedSymptoms: updatedHistory,
          name: result.name || t('advice.noAdviceData'),
          cropName: cropName // Include crop name in diagnosis data
        };

        sessionStorage.setItem('diagnosisData', JSON.stringify(diagnosisData));
        navigate(`/crops/${cropId}/disease/${diagnosisData.disId}/advice`);
        setSymptoms([]);
      } else if (result.symptoms && result.symptoms.length > 0) {
        setSymptoms(result.symptoms);
        setCurrentLevel(prevLevel => prevLevel + 1);
      } else {
        console.log("No disease found and no more symptoms available.");
        setSymptoms([]);
      }
    } catch (err) {
      console.error("Error during symptom confirmation:", err);
    } finally {
      setLoading(false);
      setPopupSymptom(null);
    }
  };

  // Reset handler needs to clear all selected symptoms
  const handleReset = () => {
    setAllSelectedSymptoms([]);
    setReturningFromAdvice(false);
    navigate('/');
  };

  return (
    <div className="flex flex-col md:flex-row p-7 gap-4">
      {/* Left panel */}
      <div className="w-full md:w-3/5 border-b md:border-b-0 md:border-r-4 border-black-300 pb-4 md:pb-0 md:pr-4">
        <div className="flex justify-between mb-3">
          <button onClick={handleBackWithHistory} className="text-blue-600 hover:underline">
            {t('common.back')}
          </button>
          <button onClick={handleReset} className="text-red-600 hover:underline">
            {t('common.startFresh')}
          </button>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          {t('symptoms.cropSymptoms', { cropName })}
        </h2>
        <p className="text-sm text-gray-600 mb-4">{t('symptoms.clickYesPrompt')}</p>

        {loading ? (
          <div className="text-center p-4">
            <p>{t('common.loading')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {symptoms.length > 0 ? (
              <>
                {symptoms.map((sym, idx) => {
                  const isSelected = isSymptomSelected(sym);
                  return (
                    <div
                      key={sym.sid}
                      className={`flex items-center justify-between p-3 rounded border transition-colors ${
                        isSelected
                          ? 'bg-green-50 border-green-200' 
                          : idx % 2 === 0 
                            ? 'bg-gray-50' 
                            : 'bg-blue-50'
                      }`}
                    >
                      <span className="font-medium flex-grow pr-4">{idx + 1}. {sym.qn}</span>
                      <button
                        onClick={() => handleYesClick(sym)}
                        disabled={loading}
                        className={`flex items-center justify-center min-w-[80px] px-3 py-1.5 border transition-colors ${
                          isSelected
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                        } disabled:opacity-50`}
                        aria-label={t('common.yes')}
                      >
                        {isSelected ? (
                          <>
                            <svg className="w-4 h-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                            </svg>
                            <span className="text-sm font-medium">{t('common.yes')}</span>
                          </>
                        ) : (
                          <span className="text-sm font-medium">{t('common.yes')}</span>
                        )}
                      </button>
                    </div>
                  );
                })}
                
                {/* Custom symptom option */}
                <div className="flex items-center justify-between p-3 rounded border bg-orange-50 border-orange-200">
                  <span className="font-medium flex-grow pr-4">{symptoms.length + 1}. {t('customSymptomQuestion')}</span>
                  <button
                    onClick={handleCustomSymptomClick}
                    disabled={loading}
                    className="flex items-center justify-center min-w-[80px] px-3 py-1.5 bg-white text-green-600 border border-green-200 hover:bg-green-50 disabled:opacity-50 transition-colors"
                  >
                    <span className="text-sm font-medium">{t('common.yes')}</span>
                  </button>
                </div>
              </>
            ) : (
              <p className="text-center p-4 text-gray-500">{t('noSymptoms')}</p>
            )}
          </div>
        )}
      </div>

      {/* Right panel */}
      <div className="w-full md:w-1/2 pl-0 md:pl-4 flex flex-col items-center">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 text-center">{t('symptoms.confirmedSymptoms')}</h2>
        {history.length === 0 ? (
          <p className="text-lg text-gray-500 text-center">{t('symptoms.none')}</p>
        ) : (
          <ul className="list-disc text-lg text-gray-800 space-y-2 w-full max-w-md">
            {history.map(s => <li key={s.sid}>{s.name}</li>)}
          </ul>
        )}
      </div>

      {/* Popup Overlay for regular symptoms */}
      {popupSymptom && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={handlePopupBack}
          />
          <div className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 z-50 w-80 shadow-lg">
            <h4 className="text-lg font-medium mb-4 text-center">{popupSymptom.qn}</h4>
            <p className="text-center text-gray-600 mb-6">
              {t('symptoms.popupConfirmation')}
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={handlePopupBack}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {t('common.back')}
              </button>
              <button
                onClick={handlePopupConfirm}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Custom Symptom Dialog */}
      {showCustomSymptomDialog && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={handleCustomSymptomDialogClose}
          />
          <div className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 z-50 w-96 shadow-lg max-h-96 overflow-y-auto">
            <h4 className="text-lg font-medium mb-4 text-center text-green-700">
              {t('customSymptomQuestion')}
            </h4>
            
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 mb-2">
                {t('healthyCondition')}
              </p>
              <p className="text-sm text-green-800 mb-2">
                {t('otherwiseText')} <strong>cropdarpan@gmail.com</strong> {t('helpRefineSystem')}
              </p>
            </div>

            <div className="text-center">
              <p className="text-lg font-semibold text-green-600 mb-4">
                {t('thankYou')}
              </p>
              <button
                onClick={handleCustomSymptomDialogClose}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {t('ok')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SymptomsPage;